'use client';

import { Link } from 'next-view-transitions';
import { usePathname, useSearchParams } from 'next/navigation';
import { BookOpen, List } from 'lucide-react';

import { placeIconButton } from './PlaceHeader';
import { cn } from '@/components/ui/utils';
import {
  buildPlaceMenuViewHref,
  type PlaceMenuViewMode,
} from '@/lib/customer/place-menu-view';

/**
 * The menu-view toggle — one button in the public place header that flips the
 * menu between the two bodies it can wear.
 *
 * THE WEB TWIN OF THE CHOW APP'S TOGGLE. `menuraq-chow-mobile` puts a single
 * icon button in the menu header that flips between the merchant's published
 * design and the plain menu list (`app/place/[id]/menu.tsx`). This is the same
 * thing: ONE button, ONE click, the body changes. No sheet, no gallery, no
 * list of designs to choose from — a guest browsing a menu is not shopping for
 * templates.
 *
 * TWO BODIES, NOT A GALLERY. The guest chooses between Menuraq's own menu and
 * the design the restaurant published for THIS branch. Choosing a design is
 * the merchant's job, done once per branch in the dashboard.
 *
 * FAST ON THE WEB, TOO. V2 swaps the already-loaded body in place and writes
 * the selected mode to browser history. That avoids a route round-trip for a
 * local presentation choice, while the URL remains shareable and Back/Forward
 * restores the prior view. Direct `?view=` arrivals are still resolved on the
 * server so the requested body is right on first paint. V1 keeps its route link.
 *
 * A branch without a renderable design has no view toggle, matching Chow
 * mobile's `hasDesign` gate. A disabled book icon advertises a mode the branch
 * cannot open and wastes one of the menu's three header actions.
 *
 * @location components/place/PlaceMenuViewSwitcher.tsx
 */

export type PlaceMenuViewSwitcherProps = {
  /** The body the guest is looking at right now. */
  view: PlaceMenuViewMode;
  /**
   * Whether a design body exists at all for this menu. False for a branch with
   * no renderable template — the control is not rendered.
   */
  canShowDesign: boolean;
  /** The restaurant's own template, named. Null when the branch has none. */
  designLabel: string | null;
  /** V2 draws the control as the Chow app's round book/list button. */
  experienceV2?: boolean;
  /** V2 changes the already-loaded body in place; V1 remains a route link. */
  onChangeView?: (view: PlaceMenuViewMode) => void;
};

export function PlaceMenuViewSwitcher({
  view,
  canShowDesign,
  designLabel,
  experienceV2 = false,
  onChangeView,
}: PlaceMenuViewSwitcherProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (!canShowDesign) {
    return null;
  }

  const toDesign: PlaceMenuViewMode = 'design';
  const toDefault: PlaceMenuViewMode = 'default';
  const nextView = view === 'design' ? toDefault : toDesign;
  const href = buildPlaceMenuViewHref(pathname, searchParams?.toString() ?? '', {
    view: nextView,
  });

  const label = nextView === 'design'
    ? `Show the restaurant's design${designLabel ? ` (${designLabel})` : ''}`
    : 'Use simple menu';

  const contents = (
    <>
      {nextView === 'design' ? (
        <BookOpen aria-hidden="true" className="size-5" />
      ) : (
        <List aria-hidden="true" className="size-5" />
      )}
    </>
  );
  const className = cn(
    placeIconButton,
    experienceV2 && 'border border-[var(--color-border-subtle)] bg-[var(--color-bg-sunken)]',
    'text-[var(--color-text-primary)] dark:text-[var(--color-text-primary)]',
  );

  if (experienceV2) {
    return (
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => onChangeView?.(nextView)}
        className={className}
      >
        {contents}
      </button>
    );
  }

  return (
    <Link
      href={href}
      aria-label={label}
      title={label}
      className={className}
    >
      {contents}
    </Link>
  );
}
